import React, { useEffect, useState } from 'react';
import { Input, Spin } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment/moment.js';
import Segmented from '../../../components/segmented/Segmented.jsx';
import Pagination from '../../../components/pagination/Pagination.jsx';
import TableWithSkeleton from '../../../components/table/TableWithSkeleton.jsx';
import ModalComponent from '../../../components/modals/ModalComponent.jsx';
import Eye from '../../../assets/Eye.jsx';
import { GetAllibApi, GetAllIBByIdApi } from '../../../api/request/ib/ibapproved.js';
import pendingColumns from '../../../columns/ib/ibApproved/pending.js';
import acceptedColumns from '../../../columns/ib/ibApproved/accepted.js';
import rejectColumns from '../../../columns/ib/ibApproved/reject.js';

export default function IBManagerMobile() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = useState(searchParams?.get('filter')?.trim() || 'Pending');
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [sorter, setSorter] = useState({ SortBy: 'createdDate', SortOrder: '' });
  const [selectedIBId, setSelectedIBId] = useState('');
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [iBDetails, setIBDetails] = useState({});
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  const columns = value === 'Approved' ? acceptedColumns : value === 'Declined' ? rejectColumns : pendingColumns;

  useEffect(() => {
    setSearchParams(`filter=${value}`);
  }, [value]);

  const getIBs = async () => {
    try {
      setIsLoading(true);
      const params = {
        PageNumber: page,
        PageSize: pageSize,
        SearchText: searchText,
        FilterText: value,
        ...sorter,
      };
      const { data } = await GetAllibApi(new URLSearchParams(params).toString());
      if (data?.success) {
        setData(
          (data?.data || []).map((item) => ({
            ...item,
            name: `${item?.firstName || ''} ${item?.lastName || ''}`.trim(),
            createdDate: item?.createdDate ? moment(item.createdDate).format('DD-MM-YYYY, hh:mm A') : '--',
            actionDate: item?.actionDate ? moment(item.actionDate).format('DD-MM-YYYY, hh:mm A') : '--',
            action:
              value === 'Approved' ? (
                <button type="button" onClick={() => navigate(`/ib/ib_details/${item?.userId}`)}>
                  <Eye />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedIBId(item?.userId);
                    setIsOpenModal(true);
                  }}
                >
                  <Eye />
                </button>
              ),
          }))
        );
        setTotalRecord(data?.totalRecords || 0);
      } else {
        setData([]);
        setTotalRecord(0);
      }
    } catch (error) {
      setData([]);
      setTotalRecord(0);
    } finally {
      setIsLoading(false);
    }
  };

  const getIBById = async () => {
    try {
      setIsDetailsLoading(true);
      const { data } = await GetAllIBByIdApi(selectedIBId);
      setIBDetails(data?.data || {});
    } catch (error) {
    } finally {
      setIsDetailsLoading(false);
    }
  };

  useEffect(() => {
    getIBs();
  }, [page, pageSize, searchText, value, sorter]);

  useEffect(() => {
    if (selectedIBId && isOpenModal) getIBById();
  }, [selectedIBId, isOpenModal]);

  const handleTableChange = (pagination, filters, tableSorter) => {
    if (tableSorter?.order) {
      setSorter({ SortBy: tableSorter?.field, SortOrder: tableSorter?.order });
    } else {
      setSorter({ SortBy: 'createdDate', SortOrder: '' });
    }
  };

  return (
    <div className="space-y-4">
      <Segmented
        options={['Pending', 'Approved', 'Declined']}
        value={value}
        setValue={(next) => {
          setValue(next);
          setPage(1);
        }}
      />

      <Input
        size="large"
        placeholder="Search IB"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onPressEnter={() => {
          setSearchText(search);
          setPage(1);
        }}
        className="rounded-xl"
      />

      {value === 'Pending' && (
        <p className="text-xs text-gray-500 px-1">Approve / reject with commission setup is available on desktop.</p>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <TableWithSkeleton columns={columns} data={data} loading={isLoading} onChange={handleTableChange} />
        </div>
      </div>

      <Pagination total={totalRecord} page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} />

      <ModalComponent isOpen={isOpenModal} setIsOpen={setIsOpenModal} title="IB Details" width="100%">
        {isDetailsLoading ? (
          <div className="flex justify-center flex-col my-10">
            <Spin size="large" />
            <p className="primary text-center mt-2">Loading...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {[
              ['Name', `${iBDetails?.firstName || ''} ${iBDetails?.lastName || ''}`.trim()],
              ['Email', iBDetails?.email],
              ['Phone', iBDetails?.phoneNo],
              ['Country', iBDetails?.countryName],
              ['Status', iBDetails?.status || value],
              ['Created', iBDetails?.createdDate ? moment(iBDetails.createdDate).format('DD-MM-YYYY LT') : null],
            ].map(([label, val]) => (
              <div key={label} className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-sm font-semibold primary mt-1 break-all">{val || '--'}</p>
              </div>
            ))}
          </div>
        )}
      </ModalComponent>
    </div>
  );
}
